import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IButtonCustomize, IButtonStyle, IButtonWidgetData, IReturnChangesWidgetEditForm, IReturnWidgetDataToSave, IWidgetReturnTranslatedValues, buttonStyleDefault, checkIfFieldDisabled, deepCopy, getButtonsStyle, makeid, removeUndefined, removeUndefinedValuesFromObject, widgetEditMode } from 'na-lib';
import { validColorValidator } from 'src/app/validators/color.validator';
import { CommentsService } from 'src/app/service/comments.service';
import { SettingsService } from 'src/app/service/settings.service';
import { UploadService } from 'src/app/service/upload.service';
import { checkUrlProtocol, urlInvalidCharactersValidator } from 'src/app/validators/formValidators.dt';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getButtonColorDefaults } from '../../../shared/utils';
import { displayArrayKeyPath, getArrayItemKeyPaths, getArrayKeyPaths } from '../../array-key-paths';

type LinkChip =
  | { kind: 'child'; id: string; label: string }
  | { kind: 'anchor'; id: string; label: string };

interface AnchorOption {
  id: string;
  label?: string;
  name?: string;
}

interface ChildPageOption {
  id: string;
  name?: string;
}

@Component({
    selector: 'app-buttons-edit-form',
    templateUrl: './buttons-edit-form.component.html',
    styleUrl: './buttons-edit-form.component.css',
    standalone: false
})

export class ButtonsEditFormComponent {
  @Input() data:widgetEditMode
  @Input() clickToSave:boolean
  @Output() returnChanges = new EventEmitter<IReturnChangesWidgetEditForm>()
  @Output() returnDataToSave = new EventEmitter<IReturnWidgetDataToSave>()
  @Output() formChange = new EventEmitter<FormGroup>();
  buttonsData:any
  oldData:any
  defaultLanguage:string
  widgetNumber:any
  passId:any
  translationModeOriginal = false
  userHasBetaWidgetRole = false
  buttonsForm!:FormGroup
  buttonsStyleData:IButtonStyle
  buttonsStyleForm!:FormGroup
  dynamicButtonFieldsForm!: FormGroup
  colorPalette:any=[]
  invalid_backgroundColor=false
  invalid_foregroundColor=false
  invalid_borderColor=false
  buttonStyleDefault = buttonStyleDefault
  newImage:any=[]
  allbuttonsForm!:FormGroup
  allButtons:any = []
  allButtonsIds:any = []
  allButtonsNumber:any = 0
  panelOpenState=false
  urlRegex = /^(?:\$\{[A-Za-z0-9._:-]+\}|#\S+|child:[^\s]+|dpp:\/\/main|(?:https?:\/\/|context:\/\/|pass:\/\/|content:\/\/|task:\/\/|tridy:\/\/)\S+)$/;
  urlRegexDoc = /^(?:\$\{[^}]+\}|pass:\/\/[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+)$/;
  urlRegexDynamic = /^\$\{[A-Za-z0-9._:-]+\}$/;

  url:any
  uploadDocument:any={}
  anchorOptions: AnchorOption[] = [];
  childPages: ChildPageOption[] = [];
  arrayKeyPaths: string[] = [];
  arrayItemKeyPaths: string[] = [];
  dataStructureMode = false;
  private rememberedArrayKeyPath = '';
  displayArrayKeyPath = displayArrayKeyPath;
  private destroy$ = new Subject<void>();
  
  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private uploadService: UploadService,
    private commentService: CommentsService,
    private translate:TranslateService,
    private settingsService: SettingsService
  ){
  }

  ngOnInit():void {
    this.dynamicButtonFieldsForm = this.fb.group({ title: [''], url: [''] });
    this.arrayKeyPaths = getArrayKeyPaths((this.data as any)?.contentTypeSchema);

    if(this.settingsService.checkIfBetaRoleAccess())
    this.userHasBetaWidgetRole = true

    this.defaultLanguage = this.data.defaultLanguage
    this.buttonsData = deepCopy(this.data.values)
    this.oldData = deepCopy(this.buttonsData)
    this.passId = this.data.passId
    this.widgetNumber= this.data.widget

    if(this.data.translateMode){
      let translateData = this.data.translateData
      const {fields, originalLanguageData, defaultLanguageWidgetData, valuesTranslated, selectedLanguage, values} = translateData

      if(translateData.originalLanguageData){
        this.translationModeOriginal = true
        this.initTranslateEditData( defaultLanguageWidgetData, valuesTranslated, originalLanguageData, [],selectedLanguage)
      }else{
        this.translationModeOriginal = false
        const data = deepCopy(values)
        this.initTranslateEditData(data,valuesTranslated, originalLanguageData, fields, selectedLanguage)
      }
    }
    else
    this.initWidgetEdit()

    this.initializePaletteColors()
    this.checkInvalidColors()

  const extra = this.data as any;
  this.anchorOptions = extra.anchorOptions || [];
  this.childPages   = extra.childPages || [];

  }

  selectArrayKeyPath(path: string): void {
    const control = this.buttonsForm.get('keyPath');
    control?.setValue(path);
    control?.markAsDirty();
    control?.markAsTouched();
    this.arrayItemKeyPaths = getArrayItemKeyPaths((this.data as any)?.contentTypeSchema, path);
    this.ensureDynamicButton();
  }

  get isArrayMode(): boolean {
    return !!this.buttonsForm?.get('keyPath')?.value?.trim();
  }

  get isDataStructureMode(): boolean {
    return this.dataStructureMode || this.isArrayMode;
  }

  selectButtonSource(index: number): void {
    this.dataStructureMode = index === 1;
    if (this.dataStructureMode) {
      if (!this.isArrayMode && this.rememberedArrayKeyPath) {
        this.buttonsForm.get('keyPath')?.setValue(this.rememberedArrayKeyPath);
      }
      this.ensureDynamicButton();
      return;
    }

    // keyPath is the persisted switch for dynamic generation. Clearing it
    // restores the regular, manually configured button list. Remember it so
    // switching tabs during this edit session does not discard the selection.
    this.rememberedArrayKeyPath = String(this.buttonsForm.get('keyPath')?.value || '').trim();
    this.buttonsForm.get('keyPath')?.setValue('');
  }

  get dynamicButton(): any | undefined {
    return this.allButtons[0];
  }

  getDynamicButtonField(field: 'title' | 'url'): string {
    return String(this.dynamicButtonFieldsForm?.get(field)?.value || '');
  }

  getDynamicButtonFieldControl(field: 'title' | 'url'): FormControl {
    return this.dynamicButtonFieldsForm.controls[field] as FormControl;
  }

  setDynamicButtonField(field: 'title' | 'url', path: string): void {
    const button = this.dynamicButton;
    if (!button) return;
    const displayControl = this.dynamicButtonFieldsForm.get(field);
    if (displayControl?.value !== path) displayControl?.setValue(path, { emitEvent: false });
    const control = this.allbuttonsForm.controls[`${button.id}.${field}`];
    control?.setValue(path.trim() ? `\${${path.trim()}}` : '');
    control?.markAsDirty();
    control?.markAsTouched();
    this.buttonsForm.markAsDirty();
  }

  private ensureDynamicButton(): void {
    if (!this.allButtons.length && !this.data?.translateMode && !this.translationModeOriginal) this.addButton();
    this.syncDynamicButtonFields();
  }

  private syncDynamicButtonFields(): void {
    const button = this.dynamicButton;
    if (!button || !this.dynamicButtonFieldsForm) return;
    (['title', 'url'] as const).forEach(field => {
      const value = this.allbuttonsForm?.controls[`${button.id}.${field}`]?.value;
      const placeholder = String(value || '').match(/^\$\{(.+)\}$/);
      this.dynamicButtonFieldsForm.get(field)?.setValue(placeholder ? placeholder[1] : String(value || ''), { emitEvent: false });
    });
  }

  buildAnchorUrl(anchor: { id: string }): string {
    if (!anchor || !anchor.id) {
      console.warn('buildAnchorUrl: invalid anchor', anchor);
      return '';
    }
    return `#${anchor.id}`;
  }

  buildChildPageUrl(child: { id: string }): string {
    if (!child || !child.id) {
      console.warn('buildChildPageUrl: invalid child', child);
      return '';
    }
    return child.id === 'main' ? 'dpp://main' : `child:${child.id}`;
  }

  setButtonUrl(button: any, value: string): void {
  if (!button || !button.id) {
    console.warn('setButtonUrl: button or button.id missing', button);
    return;
  }

  const controlName = `${button.id}.url`;

  this.allbuttonsForm.patchValue({ [controlName]: value });

  const ctrl = this.allbuttonsForm.controls[controlName];
  if (ctrl) {
    ctrl.markAsDirty();
    ctrl.markAsTouched();
    ctrl.updateValueAndValidity();
  }
}

  private makeLinkChip(value: string | null | undefined): LinkChip | null {
  if (!value) return null;

  // dpp://main
  if (value === 'dpp://main') {
    const match = this.childPages?.find(x => x.id === 'main');
    return { kind: 'child', id: 'main', label: match?.name || 'Main' };
  }

  // child:<id>
  if (value.startsWith('child:')) {
    const id = value.slice('child:'.length).trim();
    if (!id) return null;

    const match = this.childPages?.find(x => x.id === id);
    return { kind: 'child', id, label: match?.name || id };
  }

  // #<id>
  if (value.startsWith('#')) {
    const id = value.slice(1).trim();
    if (!id) return null;

    const match = this.anchorOptions?.find(x => x.id === id);
    return { kind: 'anchor', id, label: match?.label || match?.name || id };
  }

  return null; // normal url => no chip
}

getLinkChip(button: any) {
  if (!button?.id || !this.allbuttonsForm) return null;

  const controlName = `${button.id}.url`;
  const value = this.allbuttonsForm.controls[controlName]?.value as string | null | undefined;

  return this.makeLinkChip(value?.trim());
}

clearLinkChip(button: any): void {
  this.setButtonUrl(button, '');
}

  ngOnChanges() {
    if(this.clickToSave){
      if(!this.translationModeOriginal){
        const buttonsData = this.loadButtonsData()
        const buttonsStyle = getButtonsStyle(this.buttonsStyleForm.value)
        this.buttonsForm.patchValue({buttons: buttonsData,buttonsStyle })
        const data = this.buttonsForm.value
        data.keyPath = data.keyPath?.trim() || undefined
        const clean:IButtonWidgetData = removeUndefinedValuesFromObject(data)
        this.returnDataToSave.emit({type:this.data.type, data: clean})
      }else{
        const buttonsData = this.loadButtonsData()
        this.buttonsForm.patchValue({buttons: buttonsData })
        const data = this.buttonsForm.value
        const clean:IButtonWidgetData = removeUndefinedValuesFromObject(data)
      }

    }
  }

  initTranslateEditData(data, valuesTranslated, originalLanguageData, fields, selectedLanguage){
    const {buttons,filter, buttonsStyle, cornerRadius, id, type, name, keyPath, widgetStyle} = data
    this.allbuttonsForm = this.fb.group({})
    const colorDefaults = getButtonColorDefaults();


    if(buttonsStyle){
      this.buttonsStyleData = buttonsStyle

      const {borderWidth, lightColors, darkColors, backgroundColor, foregroundColor, borderColor} = this.buttonsStyleData

      this.buttonsStyleForm = this.fb.group({
        backgroundColor:[ {value: lightColors?.background ? lightColors?.background : ( backgroundColor ? backgroundColor: colorDefaults.light.background), disabled:checkIfFieldDisabled('backgroundColor', fields, originalLanguageData)}, [validColorValidator()]],
        borderColor:[{value: lightColors?.border ? lightColors?.border : ( borderColor ? borderColor : colorDefaults.light.border), disabled:checkIfFieldDisabled('borderColor', fields, originalLanguageData)}, [validColorValidator()]],
        foregroundColor:[{value: lightColors?.primary ? lightColors?.primary : ( foregroundColor ? foregroundColor : colorDefaults.light.primary), disabled:checkIfFieldDisabled('foregroundColor', fields, originalLanguageData)}, [validColorValidator()]],
  
        backgroundColorDark:[{value: darkColors?.background ? darkColors?.background : colorDefaults.dark.background, disabled:checkIfFieldDisabled('backgroundColorDark', fields, originalLanguageData)}, [validColorValidator()]],
        borderColorDark:[{value: darkColors?.border ? darkColors?.border : colorDefaults.dark.border, disabled:checkIfFieldDisabled('borderColorDark', fields, originalLanguageData)}, [validColorValidator()]],
        foregroundColorDark:[{value: darkColors?.primary ? darkColors?.primary : colorDefaults.dark.primary, disabled:checkIfFieldDisabled('foregroundColorDark', fields, originalLanguageData)}, [validColorValidator()]],
  
        borderWidth:[{value: borderWidth ? borderWidth : 0, disabled:checkIfFieldDisabled('borderWidth', fields, originalLanguageData)}],
      })
      
    }else{
      this.buttonsStyleData = this.buttonStyleDefault
      
      const { borderWidth, lightColors, darkColors} = this.buttonsStyleData

      this.buttonsStyleForm = this.fb.group({
        backgroundColor:[ {value: lightColors?.background ? lightColors?.background : colorDefaults.light.background, disabled:checkIfFieldDisabled('backgroundColor', fields, originalLanguageData)}, [validColorValidator()]],
        borderColor:[{value:lightColors?.border ? lightColors?.border : colorDefaults.light.border, disabled:checkIfFieldDisabled('borderColor', fields, originalLanguageData)}, [validColorValidator()]],
        foregroundColor:[{value: lightColors?.primary ? lightColors?.primary : colorDefaults.light.primary , disabled:checkIfFieldDisabled('foregroundColor', fields, originalLanguageData)}, [validColorValidator()]],
  
        backgroundColorDark:[{value: darkColors?.background ? darkColors?.background : colorDefaults.dark.background, disabled:checkIfFieldDisabled('backgroundColorDark', fields, originalLanguageData)}, [validColorValidator()]],
        borderColorDark:[{value: darkColors?.border ? darkColors?.border : colorDefaults.dark.border, disabled:checkIfFieldDisabled('borderColorDark', fields, originalLanguageData)}, [validColorValidator()]],
        foregroundColorDark:[{value: darkColors?.primary ? darkColors?.primary : colorDefaults.dark.primary, disabled:checkIfFieldDisabled('foregroundColorDark', fields, originalLanguageData)}, [validColorValidator()]],
  
        borderWidth:[{value: borderWidth ? borderWidth : 0, disabled:checkIfFieldDisabled('borderWidth', fields, originalLanguageData)}],
      })
    }

    this.buttonsForm = this.fb.group({
      buttons:[{value: buttons ? buttons : [], disabled:checkIfFieldDisabled('buttons', fields, originalLanguageData)}],
      buttonsStyle:[{value: buttonsStyle ? buttonsStyle : undefined, disabled:checkIfFieldDisabled('buttonsStyle', fields, originalLanguageData)}],
      cornerRadius:[{value: cornerRadius ? cornerRadius : 0, disabled:checkIfFieldDisabled('cornerRadius', fields, originalLanguageData)}],
      id:[{value: id ? id : undefined, disabled:checkIfFieldDisabled('id', fields, originalLanguageData)}],
      type:[{value: type ? type : undefined, disabled:checkIfFieldDisabled('type', fields, originalLanguageData)}],
      name:[{value: name ? name : undefined, disabled:checkIfFieldDisabled('name', fields, originalLanguageData)}],
      keyPath:[{value: keyPath ?? '', disabled: true}],
      filter:[{value: filter ? filter: undefined, disabled:checkIfFieldDisabled('filter', fields, originalLanguageData)}],
      widgetStyle:[{value: widgetStyle ? widgetStyle : undefined, disabled:checkIfFieldDisabled('widgetStyle', fields, originalLanguageData)}]
    })

    if(buttons){
      const allbuttons = deepCopy(buttons)
      allbuttons.forEach( (button, index) => {
        this.allButtons.push(button)
        this.allButtonsIds.push(button.id)
        this.allButtonsNumber++
        const buttonName = button.id

        const buttonId = button.id
        const title = originalLanguageData ? button.title : valuesTranslated[selectedLanguage +'.'+id +'_'+buttonId+'.title']
        const url = originalLanguageData ? button.url : valuesTranslated[selectedLanguage +'.'+id+'_'+buttonId+'.url']

        //-------------light
        const btn_backgroundColor = buttonName + '.' + 'backgroundColor'
        const backgroundColor_value = button?.lightColors?.background ? button?.lightColors?.background : (button.backgroundColor ? button.backgroundColor : this.buttonsStyleForm.value.backgroundColor)
        this.allbuttonsForm.addControl(btn_backgroundColor, new FormControl({value:backgroundColor_value, disabled: checkIfFieldDisabled('button.backgroundColor', fields, originalLanguageData)}, [validColorValidator()]))

        const btn_foregroundColor = buttonName + '.' + 'foregroundColor'
        const foregroundColor_value = button?.lightColors?.primary ? button?.lightColors?.primary  : (button.foregroundColor ? button.foregroundColor : this.buttonsStyleForm.value.foregroundColor)
        this.allbuttonsForm.addControl(btn_foregroundColor, new FormControl({value:foregroundColor_value, disabled: checkIfFieldDisabled('button.foregroundColor', fields, originalLanguageData)}, [validColorValidator()]))

        const btn_borderColor = buttonName + '.' + 'borderColor'
        const borderColor_value =  button?.lightColors?.border ? button?.lightColors?.border : (button.borderColor ? button.borderColor : this.buttonsStyleForm.value.borderColor)
        this.allbuttonsForm.addControl(btn_borderColor, new FormControl({value:borderColor_value, disabled: checkIfFieldDisabled('button.borderColor', fields, originalLanguageData)}, [validColorValidator()]))

        //-------------dark
        const btn_backgroundColorDark = buttonName + '.' + 'backgroundColorDark'
        const backgroundColorDark_value = button?.darkColors?.background ? button?.darkColors?.background : undefined//(button?.lightColors?.background ? button?.lightColors?.background : undefined)
        this.allbuttonsForm.addControl(btn_backgroundColorDark, new FormControl({value:backgroundColorDark_value, disabled: checkIfFieldDisabled('button.backgroundColorDark', fields, originalLanguageData)}, [validColorValidator()]))

        const btn_foregroundColorDark = buttonName + '.' + 'foregroundColorDark'
        const foregroundColorDark_value = button?.darkColors?.primary ? button?.darkColors?.primary : undefined //(button?.lightColors?.primary ? button?.lightColors?.primary : undefined)
        this.allbuttonsForm.addControl(btn_foregroundColorDark, new FormControl({value:foregroundColorDark_value, disabled: checkIfFieldDisabled('button.foregroundColorDark', fields, originalLanguageData)}, [validColorValidator()]))

        const btn_borderColorDark = buttonName + '.' + 'borderColorDark'
        const borderColorDark_value = button?.darkColors?.border ? button?.darkColors?.border : undefined//(button?.lightColors?.border ? button?.lightColors?.border : undefined)
        this.allbuttonsForm.addControl(btn_borderColorDark, new FormControl({value: borderColorDark_value, disabled: checkIfFieldDisabled('button.borderColorDark', fields, originalLanguageData)}, [validColorValidator()]))

        //-------------
        const btn_borderWidth = buttonName + '.' + 'borderWidth'
        const borderWidth_value = button.borderWidth ? button.borderWidth : this.buttonsStyleForm.value.borderWidth
        this.allbuttonsForm.addControl(btn_borderWidth, new FormControl({value: borderWidth_value, disabled: checkIfFieldDisabled('button.borderWidth', fields, originalLanguageData)}))

        const btn_customize = buttonName + '.' + 'customize'
        this.allbuttonsForm.addControl(btn_customize, new FormControl({value:button.customize, disabled: checkIfFieldDisabled('button.customize', fields, originalLanguageData)}))

        const btn_id = buttonName + '.' + 'id'
        this.allbuttonsForm.addControl(btn_id, new FormControl({value:button.id, disabled: false/* checkIfFieldDisabled('button.id', fields, originalLanguageData) */}))

        const btn_url = buttonName + '.' + 'url'
        this.allbuttonsForm.addControl( btn_url, new FormControl({value:url, disabled: checkIfFieldDisabled('button.url', fields, originalLanguageData)}, [ checkUrlProtocol, Validators.pattern(this.urlRegex), urlInvalidCharactersValidator() ]) );

        const btn_title = buttonName + '.' + 'title'
        this.allbuttonsForm.addControl(btn_title, new FormControl({value:title, disabled: checkIfFieldDisabled('button.title', fields, originalLanguageData)}))
      })
    }


    this.buttonsStyleForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( values => this.updateFromData('buttonsStyle', values) )

    this.buttonsForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if(!originalLanguageData)
        this.returnChanges.emit({
          type:"buttons",
          form: this.buttonsForm,
          validForm: this.buttonsForm.valid,
          translations:value,
          saveDisabled: false
        })
    })

    this.allbuttonsForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formChange.emit(this.allbuttonsForm);
    });

  }

  initWidgetEdit(){
    const {buttons,filter, buttonsStyle, cornerRadius, id, type, name, keyPath, widgetStyle} = this.buttonsData
    this.allbuttonsForm = this.fb.group({})
    const colorDefaults = getButtonColorDefaults();

    if(this.buttonsData){

      if(buttonsStyle){
        this.buttonsStyleData = buttonsStyle

        const {borderWidth, lightColors, darkColors, backgroundColor, foregroundColor, borderColor} = this.buttonsStyleData

        this.buttonsStyleForm = this.fb.group({
          backgroundColor:[ {value: lightColors?.background ? lightColors?.background : ( backgroundColor ? backgroundColor: colorDefaults.light.background), disabled:false}, [validColorValidator()]],
          borderColor:[{value: lightColors?.border ? lightColors?.border : ( borderColor ? borderColor : colorDefaults.light.border), disabled:false}, [validColorValidator()]],
          foregroundColor:[{value: lightColors?.primary ? lightColors?.primary : ( foregroundColor ? foregroundColor : colorDefaults.light.primary), disabled:false}, [validColorValidator()]],
    
          backgroundColorDark:[{value: darkColors?.background ? darkColors?.background : colorDefaults.dark.background, disabled:false}, [validColorValidator()]],
          borderColorDark:[{value: darkColors?.border ? darkColors?.border : colorDefaults.dark.border, disabled:false}, [validColorValidator()]],
          foregroundColorDark:[{value: darkColors?.primary ? darkColors?.primary : colorDefaults.dark.primary, disabled:false}, [validColorValidator()]],
    
          borderWidth:[{value: borderWidth ? borderWidth : 0, disabled:false}],
        })
        
      }else{
        this.buttonsStyleData = this.buttonStyleDefault
        
        const { borderWidth, lightColors, darkColors} = this.buttonsStyleData

        this.buttonsStyleForm = this.fb.group({
          backgroundColor:[ {value: lightColors?.background ? lightColors?.background : colorDefaults.light.background, disabled:false}, [validColorValidator()]],
          borderColor:[{value:lightColors?.border ? lightColors?.border : colorDefaults.light.border, disabled:false}, [validColorValidator()]],
          foregroundColor:[{value: lightColors?.primary ? lightColors?.primary : colorDefaults.light.primary , disabled:false}, [validColorValidator()]],
    
          backgroundColorDark:[{value: darkColors?.background ? darkColors?.background : colorDefaults.dark.background, disabled:false}, [validColorValidator()]],
          borderColorDark:[{value: darkColors?.border ? darkColors?.border : colorDefaults.dark.border, disabled:false}, [validColorValidator()]],
          foregroundColorDark:[{value: darkColors?.primary ? darkColors?.primary : colorDefaults.dark.primary, disabled:false}, [validColorValidator()]],
    
          borderWidth:[{value: borderWidth ? borderWidth : 0, disabled:false}],
        })
      }

      this.buttonsForm = this.fb.group({
        buttons:[{value: buttons ? buttons : [], disabled:false}],
        buttonsStyle:[{value: buttonsStyle ? buttonsStyle : undefined, disabled:false}],
        cornerRadius:[{value: cornerRadius ? cornerRadius : 0, disabled:false}],
        id:[{value: id ? id : undefined, disabled:false}],
        type:[{value: type ? type : undefined, disabled:false}],
        name:[{value: name ? name : undefined, disabled:false}],
        keyPath:[{value: keyPath ?? '', disabled:false}],
        filter:[{value: filter ? filter: undefined, disabled:false}],
        widgetStyle:[{value: widgetStyle ? widgetStyle : undefined, disabled:false}]
      })

      if(buttons){
        buttons.forEach( (button, index) => {
          this.allButtons.push(button)
          this.allButtonsIds.push(button.id)
          this.allButtonsNumber++
          const buttonName = button.id
  
          //-------------light
          const btn_backgroundColor = buttonName + '.' + 'backgroundColor'
          const backgroundColor_value = button?.lightColors?.background ? button?.lightColors?.background : (button.backgroundColor ? button.backgroundColor : this.buttonsStyleForm.value.backgroundColor)
          this.allbuttonsForm.addControl(btn_backgroundColor, new FormControl(backgroundColor_value, [validColorValidator()]))

          const btn_foregroundColor = buttonName + '.' + 'foregroundColor'
          const foregroundColor_value = button?.lightColors?.primary ? button?.lightColors?.primary  : (button.foregroundColor ? button.foregroundColor : this.buttonsStyleForm.value.foregroundColor)
          this.allbuttonsForm.addControl(btn_foregroundColor, new FormControl(foregroundColor_value, [validColorValidator()]))

          const btn_borderColor = buttonName + '.' + 'borderColor'
          const borderColor_value =  button?.lightColors?.border ? button?.lightColors?.border : (button.borderColor ? button.borderColor : this.buttonsStyleForm.value.borderColor)
          this.allbuttonsForm.addControl(btn_borderColor, new FormControl(borderColor_value, [validColorValidator()]))

          //-------------dark
          const btn_backgroundColorDark = buttonName + '.' + 'backgroundColorDark'
          const backgroundColorDark_value = button?.darkColors?.background ? button?.darkColors?.background : undefined//(button?.lightColors?.background ? button?.lightColors?.background : undefined)
          this.allbuttonsForm.addControl(btn_backgroundColorDark, new FormControl(backgroundColorDark_value, [validColorValidator()]))

          const btn_foregroundColorDark = buttonName + '.' + 'foregroundColorDark'
          const foregroundColorDark_value = button?.darkColors?.primary ? button?.darkColors?.primary : undefined //(button?.lightColors?.primary ? button?.lightColors?.primary : undefined)
          this.allbuttonsForm.addControl(btn_foregroundColorDark, new FormControl(foregroundColorDark_value, [validColorValidator()]))

          const btn_borderColorDark = buttonName + '.' + 'borderColorDark'
          const borderColorDark_value = button?.darkColors?.border ? button?.darkColors?.border : undefined//(button?.lightColors?.border ? button?.lightColors?.border : undefined)
          this.allbuttonsForm.addControl(btn_borderColorDark, new FormControl(borderColorDark_value, [validColorValidator()]))

          //-------------
          const btn_borderWidth = buttonName + '.' + 'borderWidth'
          const borderWidth_value = button.borderWidth ? button.borderWidth : this.buttonsStyleForm.value.borderWidth
          this.allbuttonsForm.addControl(btn_borderWidth, new FormControl(borderWidth_value))

          const btn_customize = buttonName + '.' + 'customize'
          this.allbuttonsForm.addControl(btn_customize, new FormControl(button.customize))

          const btn_id = buttonName + '.' + 'id'
          this.allbuttonsForm.addControl(btn_id, new FormControl(button.id))

          const btn_url = buttonName + '.' + 'url'
          this.allbuttonsForm.addControl( btn_url, new FormControl(button.url, [ checkUrlProtocol, Validators.pattern(this.urlRegex), urlInvalidCharactersValidator() ]) );

          const btn_title = buttonName + '.' + 'title'
          this.allbuttonsForm.addControl(btn_title, new FormControl(button.title))
        })
      }

      this.arrayItemKeyPaths = getArrayItemKeyPaths((this.data as any)?.contentTypeSchema, keyPath);
      this.syncDynamicButtonFields();
      this.buttonsForm.get('keyPath')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(path => {
        this.arrayItemKeyPaths = getArrayItemKeyPaths((this.data as any)?.contentTypeSchema, path);
        if (String(path || '').trim()) this.ensureDynamicButton();
      });
      if (String(keyPath || '').trim()) this.ensureDynamicButton();

      this.buttonsStyleForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe( values => this.updateFromData('buttonsStyle', values) )

      this.buttonsForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
        this.returnChanges.emit({
          type:"buttons",
          form: this.buttonsForm,
          validForm: this.buttonsForm.valid,
          data:value,
          saveDisabled: !this.buttonsForm.valid || (this.invalid_backgroundColor || this.invalid_borderColor || this.invalid_foregroundColor || !this.buttonsStyleForm.valid || !this.allbuttonsForm.valid)
        })
      })
    }

    this.buttonsStyleForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(values => {
      this.checkInvalidColors();
      this.emitNonTranslationChanges();
    });

    this.allbuttonsForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(values => {
      this.checkInvalidColors();
      this.emitNonTranslationChanges();
    });
  }

  loadButtonsData(){
    let newbuttons = []

    this.allButtonsIds.forEach((buttonId, index) => {
      const buttonIdData =  Object.fromEntries( 
        Object.entries(this.allbuttonsForm.value).filter(([buttonFieldData]) => buttonFieldData.includes(buttonId))
          .map(([key, value]) => {
            const newKey = key.replace(`${buttonId}.`, '') ;
            return [newKey, value];
          })
        );

      const {backgroundColor, backgroundColorDark, foregroundColor, foregroundColorDark, borderColor, borderColorDark, url, title, customize, borderWidth, id} = buttonIdData

      const buttonToAdd:IButtonCustomize = {
        lightColors: customize ? {
          background : customize ? backgroundColor : undefined, 
          primary : customize ? foregroundColor : undefined, 
          border : customize ? borderColor : undefined, 
        } : undefined,
        darkColors: customize ? {
          background : customize ? (backgroundColorDark ? backgroundColorDark : undefined) : undefined, 
          primary : customize ? (foregroundColorDark ? foregroundColorDark : undefined) : undefined, 
          border : customize ? (borderColorDark ? borderColorDark : undefined) : undefined, 
        } : undefined,
        id:id,
        title: title ? title : undefined, 
        url: url ? String(url) : undefined,
        customize: customize ? customize : false,
        borderWidth : customize ? borderWidth : 0
      }

      removeUndefined(buttonToAdd)
      if(customize && buttonToAdd.darkColors && !buttonToAdd.darkColors.background && !buttonToAdd.darkColors.primary && !buttonToAdd.darkColors.border)
       delete buttonToAdd.darkColors

      newbuttons.push(buttonToAdd)
    });

    return newbuttons
  }

  private emitNonTranslationChanges() {
  if (!this.translationModeOriginal && this.buttonsForm) {
    const buttonsData = this.loadButtonsData();
    const buttonsStyle = getButtonsStyle(this.buttonsStyleForm.value);

    this.buttonsForm.patchValue({ buttons: buttonsData, buttonsStyle });

    const data = this.buttonsForm.value;

    this.returnChanges.emit({
      type: "buttons",
      form: this.buttonsForm,
      validForm: this.buttonsForm.valid,
      data: data,
      saveDisabled: !this.buttonsForm.valid || this.invalid_backgroundColor || this.invalid_borderColor || this.invalid_foregroundColor || !this.buttonsStyleForm.valid || !this.allbuttonsForm.valid
    });
  }
}

  updateFromData(field, values){
    this.buttonsForm.patchValue({[field]: values}/*, { emitEvent: false }*/)
  }

  initializePaletteColors(){
    var colors=[]
    const warmOrange = getComputedStyle(document.documentElement).getPropertyValue('--warmOrange')
    const darkblue = getComputedStyle(document.documentElement).getPropertyValue('--darkBlue')
    const pureWhite = getComputedStyle(document.documentElement).getPropertyValue('--pureWhite')
    const neutral = getComputedStyle(document.documentElement).getPropertyValue('--neutral')
    colors.push(warmOrange)
    colors.push(darkblue)
    colors.push(pureWhite)
    colors.push(neutral)
    this.colorPalette=colors
  }

  checkInvalidColors() {
    const buttonColorControls = ['backgroundColor', 'borderColor', 'foregroundColor'];
  
    this.invalid_backgroundColor = false;
    this.invalid_foregroundColor = false;
    this.invalid_borderColor = false;
  
    this.allButtons.forEach(button => {
      buttonColorControls.forEach(control => {
        const controlName = `${button.id}.${control}`;
        if (this.allbuttonsForm.controls[controlName]?.hasError('invalidColor')) {
          // Set error flags
          if (control === 'backgroundColor') this.invalid_backgroundColor = true;
          if (control === 'borderColor') this.invalid_borderColor = true;
          if (control === 'foregroundColor') this.invalid_foregroundColor = true;
        }
      });
    });
  
    buttonColorControls.forEach(control => {
      if (this.buttonsStyleForm.controls[control]?.hasError('invalidColor')) {
        // Set error flags
        if (control === 'backgroundColor') this.invalid_backgroundColor = true;
        if (control === 'borderColor') this.invalid_borderColor = true;
        if (control === 'foregroundColor') this.invalid_foregroundColor = true;
      }
    });
  }

  deleteBtn(indexSelected, id){
    this.allButtons.splice(indexSelected,1)
    this.allButtonsIds.splice(indexSelected,1)

    const btn_backgroundColor = id + '.' + 'backgroundColor'
    const btn_foregroundColor = id + '.' + 'foregroundColor'
    const btn_borderColor = id + '.' + 'borderColor'
    const btn_borderWidth = id + '.' + 'borderWidth'
    const btn_customize = id + '.' + 'customize'
    const btn_id = id + '.' + 'id'
    const btn_url = id + '.' + 'url'
    const btn_title = id + '.' + 'title'
    this.allbuttonsForm.removeControl(btn_backgroundColor)
    this.allbuttonsForm.removeControl(btn_foregroundColor)
    this.allbuttonsForm.removeControl(btn_borderColor)
    this.allbuttonsForm.removeControl(btn_borderWidth)
    this.allbuttonsForm.removeControl(btn_customize)
    this.allbuttonsForm.removeControl(btn_id)
    this.allbuttonsForm.removeControl(btn_url)
    this.allbuttonsForm.removeControl(btn_title)
  }

  addButton(){
    const colorDefaults = getButtonColorDefaults();
    let button = { customize: false, id: makeid(8), title: "Button", url: "" }

    this.allButtons.push(button)
    this.allButtonsIds.push(button.id)
    const buttonNum = deepCopy(button.id)

    const btn_backgroundColor = buttonNum + '.' + 'backgroundColor'
    this.allbuttonsForm.addControl(btn_backgroundColor, new FormControl(this.buttonsStyleForm?.value?.backgroundColor || colorDefaults.light.background))

    const btn_foregroundColor = buttonNum + '.' + 'foregroundColor'
    this.allbuttonsForm.addControl(btn_foregroundColor, new FormControl(this.buttonsStyleForm?.value?.foregroundColor || colorDefaults.light.primary))

    const btn_borderColor = buttonNum + '.' + 'borderColor'
    this.allbuttonsForm.addControl(btn_borderColor, new FormControl(this.buttonsStyleForm?.value?.borderColor || colorDefaults.light.border))

    const btn_backgroundColorDark = buttonNum + '.' + 'backgroundColorDark'
    this.allbuttonsForm.addControl(btn_backgroundColorDark, new FormControl(this.buttonsStyleForm?.value?.backgroundColorDark || colorDefaults.dark.background))

    const btn_foregroundColorDark = buttonNum + '.' + 'foregroundColorDark'
    this.allbuttonsForm.addControl(btn_foregroundColorDark, new FormControl(this.buttonsStyleForm?.value?.foregroundColorDark || colorDefaults.dark.primary))

    const btn_borderColorDark = buttonNum + '.' + 'borderColorDark'
    this.allbuttonsForm.addControl(btn_borderColorDark, new FormControl(this.buttonsStyleForm?.value?.borderColorDark || colorDefaults.dark.border))

    const btn_borderWidth = buttonNum + '.' + 'borderWidth'
    this.allbuttonsForm.addControl(btn_borderWidth, new FormControl(0))

    const btn_customize = buttonNum + '.' + 'customize'
    this.allbuttonsForm.addControl(btn_customize, new FormControl(button.customize))

    const btn_id = buttonNum + '.' + 'id'
    this.allbuttonsForm.addControl(btn_id, new FormControl(button.id))

    const btn_url = buttonNum + '.' + 'url';
    
    this.allbuttonsForm.addControl( btn_url, new FormControl(button.url, [
              checkUrlProtocol,
              Validators.pattern(this.urlRegex),
              urlInvalidCharactersValidator()
            ]) );

    const btn_title = buttonNum + '.' + 'title'
    this.allbuttonsForm.addControl(btn_title, new FormControl(button.title))
    this.allButtonsNumber++
  }

  readDocument(event: any, button: any) {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/webp',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'text/csv',
        'text/vcard',
        'application/rtf'
      ];
      
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        const message = this.translate.instant("SnackBarConstants.INVALID_FILE_FORMAT");
        this.commentService.addSnackBar.emit(message);
        return;
      }
      
      // Optional: Validate file size
      const maxSizeInBytes = 10 * 1024 * 1024; // Example: 10 MB size limit
      if (file.size > maxSizeInBytes) {
        const message = this.translate.instant("SnackBarConstants.FILE_TOO_LARGE");
        this.commentService.addSnackBar.emit(message);
        return;
      }
      
      // Proceed with reading and uploading the file
      const reader = new FileReader();
      this.newImage = file;
      reader.readAsDataURL(file); // Read file as data URL
      
      reader.onload = async (event) => { // Called once readAsDataURL is completed
        if (event.target) {
          const urlNew = event.target.result;
  
          try {
            const uploadURL = await this.uploadService.uploadDocument(this.passId, this.newImage, this.widgetNumber);
            
            if (uploadURL) {
              this.url = uploadURL;
              const name = button.id + '.url';
              this.allbuttonsForm.patchValue({
                [name]: this.url
              });
              this.uploadDocument[button.id] = true;
            }
          } catch (error) {
            console.error('Error uploading document', error);
          }
        }
      };
    }
  }

  getCustomizeOptionOfButton(index,id){
    const name = id+'.customize'
    return this.allbuttonsForm.value[name]
  }

  returnChangesStyle($event){
    this.buttonsForm.patchValue({widgetStyle: $event.newStyle})
  }

  returnChangesFilters($event){
    this.buttonsForm.patchValue({filter: $event.newFilters})
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
